import React, { useEffect, memo, useRef, useState } from 'react'
import styles from './LiveGamesPage.module.css'
import Championship from './components/Championship'
import { VariableSizeList as List, areEqual } from 'react-window';
import TopBar from '../../common/components/TopBar'
import SectionSelector from './components/SectionSelector'
import {
  SPORT_TYPES_HEIGHT,
  LIVE_GAME_HEIGHT,
  CHAMPIONSHIP_HEAD_HEIGHT,
  TOP_BAR_HEIGHT,
  ADDITIONAL_RESULTS_SPORTTYPE,
  SUB_SPORT_TYPES_HEIGHT, SECTION_HEIGHT, SHOW_SPORT_ICON_SPORT_TYPES, DEFAULT_COLLAPSED_SECTIONS, FAVORITES_SPORTTYPE
} from '../../app/const'
import useTranslation from '../../hooks/translations'
import SubSportTypes from './components/SubSportTypes'
import { LiveChampionships, Sports, SubSports } from './types'
import memoize from 'memoize-one'
import NoLiveGames from './components/NoLiveGames'
import SportTypesContainer from './components/SportTypesContainer'
import { debounce } from 'lodash';
import { toggleIsSearch, toggleIsSearching } from './LiveSlice';
import { useDispatch } from 'react-redux';

interface Props {
  currentGames: boolean
  currentSportType: string
  currentSubSportType: string | null
  sportTypes: Sports[]
  liveGames: LiveChampionships[]
  collapsedChampionships: number[]
  subSportTypes: SubSports[] | null
  collapsedSections: typeof DEFAULT_COLLAPSED_SECTIONS
  urlSportType: string
  isSearching: boolean
}
interface ChampionshipRowProps {
  index: number
  style: React.CSSProperties
  data: { items: LiveChampionships[] }
  isScrolling?: boolean
}

const LiveGamesPage = ({
  currentGames,
  currentSportType,
  currentSubSportType,
  sportTypes,
  liveGames,
  collapsedChampionships,
  subSportTypes,
  collapsedSections,
  urlSportType,
  isSearching,
}: Props) => {
  const { t } = useTranslation('live')
  const dispatch = useDispatch()
  const refList = useRef() as React.MutableRefObject<List>
  const createItemData = memoize((items: LiveChampionships[]) => ({ items }))
  const itemData = createItemData(liveGames)
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  })
  const scrollTopLiveList = (index: number) => {
    refList.current.scrollToItem(index)
  }
  useEffect(() => {
    if (refList?.current) refList.current.resetAfterIndex(0)
  }, [
    itemData,
    collapsedChampionships,
    collapsedSections,
    currentSportType,
    currentSubSportType,
  ])

  const ChampionshipRow = memo(
    ({ data, index, style, isScrolling }: ChampionshipRowProps) => {
      const { items } = data
      const item = items.length !== 0 ? items[index - 2] : null
      const disableSearching = debounce(() => {
        dispatch(toggleIsSearching(false))
      }, 10)        
      if (isScrolling && isSearching) disableSearching()
      
      if (index === 0) {
        return (
          <>
            <TopBar />
            {subSportTypes ? (
              <SubSportTypes
                sports={subSportTypes}
                currentSport={currentSportType}
                currentSubSport={currentSubSportType}
              />
            ) : (
              ''
            )}
          </>
        )
      } else if (index === 1) {
        return (
          <>
            {currentSportType !== String(FAVORITES_SPORTTYPE) ? (
              <SectionSelector
                title={t('mobile.live.section.CurrentGames')}
                isCurrentGames={true}
                isActive={collapsedSections.current}
                style={style}
              />
            ) : (
              ''
            )}
            {!collapsedSections.current ||
            currentSportType !== String(FAVORITES_SPORTTYPE) ? (
              <SectionSelector
                title={t('mobile.live.section.TodaysGames')}
                isCurrentGames={false}
                isActive={collapsedSections.today}
                style={style}
              />
            ) : (
              ''
            )}
          </>
        )
      } else if (index >= items.length + 2) {
        return collapsedSections.current ||
          currentSportType !== String(FAVORITES_SPORTTYPE) ? (
          <SectionSelector
            title={t('mobile.live.section.TodaysGames')}
            isCurrentGames={false}
            isActive={collapsedSections.today}
            style={style}
          />
        ) : (
          <></>
        )
      } else {
        return item !== null &&
          (collapsedSections.current || collapsedSections.today) ? (
          <Championship
            key={item.championatId}
            Name={item.Name}
            ChampClass={item.ChampClass}
            TypeId={item.TypeId}
            championatId={item.championatId}
            Games={item.Games}
            additionalResults={item.TypeId === ADDITIONAL_RESULTS_SPORTTYPE}
            showSportType={SHOW_SPORT_ICON_SPORT_TYPES.includes(
              currentSportType
            )}
            // style={{...style, transition: 'all 250ms ease-in-out', overflow: 'hidden' }}
            style={style}
          />
        ) : (
          <></>
        )
      }
    },
    areEqual
  )

  const getItemSize = (index: number) => {
    const i = index - 2
    return index === 0
      ? TOP_BAR_HEIGHT +
          (subSportTypes ? SUB_SPORT_TYPES_HEIGHT : 0) +
          (currentSportType !== String(FAVORITES_SPORTTYPE)
            ? SECTION_HEIGHT
            : 0)
      : index === 1
      ? collapsedSections.current
        ? 0
        : currentSportType !== String(FAVORITES_SPORTTYPE)
        ? SECTION_HEIGHT
        : 0
      : index >= liveGames.length + 2
      ? collapsedSections.current
        ? currentSportType !== String(FAVORITES_SPORTTYPE)
          ? SECTION_HEIGHT
          : 0
        : 0
      : collapsedSections.current || collapsedSections.today
      ? liveGames.length > 0
        ? (collapsedChampionships.includes(liveGames[i].championatId)
            ? 0
            : liveGames[i].Games.length * LIVE_GAME_HEIGHT) +
          CHAMPIONSHIP_HEAD_HEIGHT
        : 0
      : 0
  }

  useEffect(() => {
    const onResize = debounce(() => {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      })
      dispatch(toggleIsSearching(false))
    }, 100)

    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  })

  if (!liveGames) liveGames = []

  return (
    <>
      <SportTypesContainer
        sportTypes={sportTypes}
        currentSportType={currentSportType}
        scrollTopLiveList={scrollTopLiveList}
        urlSportType={urlSportType}
      />
      {liveGames?.length > 0 ? (
        <List
          ref={refList}
          className={styles.championshipContainer}
          itemCount={Math.max(3, liveGames.length + 3)}
          itemSize={getItemSize}
          height={dimensions.height - SPORT_TYPES_HEIGHT}
          width={dimensions.width}
          itemData={liveGames.length > 0 ? itemData : { items: [] }}
          useIsScrolling
          // {...cellMeasurerProps}
        >
          {ChampionshipRow}
        </List>
      ) : (
        <NoLiveGames />
      )}
    </>
  )
}

export default React.memo(LiveGamesPage)